/**
 * Authentication Service - Gold Standard Frontend Auth Pattern
 *
 * This service provides a clean abstraction over Supabase authentication
 * while maintaining the gold standard patterns for frontend services.
 *
 * Key Principles:
 * 1. Single Responsibility - Authentication operations only
 * 2. Consistent Error Handling - Standardized error processing
 * 3. Session Management - Secure token handling
 * 4. Logging Integration - Development logging support
 * 5. Type Safety - JSDoc for better IDE support
 */

import { supabase } from "../../lib/supabase";
import logger from "../utils/logger";

// Derive Supabase localStorage session key dynamically from project URL
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
let SUPABASE_PROJECT_REF = "";
try {
  const host = new URL(SUPABASE_URL).host; // e.g., ngmdohoikqfsddhwjyzd.supabase.co
  SUPABASE_PROJECT_REF = host.split(".")[0];
} catch {}
const SUPABASE_SESSION_KEY = SUPABASE_PROJECT_REF
  ? `sb-${SUPABASE_PROJECT_REF}-auth-token`
  : null;

class AuthService {
  constructor() {
    this.serviceName = "AuthService";
    this.enableLogging = import.meta.env.DEV;

    // Session cache to reduce duplicate calls
    this.sessionCache = null;
    this.sessionCacheTime = 0;
    this.sessionCacheDuration = 3000; // 3 seconds, align with AuthContext

    // Listen to auth state changes to keep session in sync
    this.currentSession = null;
    this.setupAuthListener();
  }

  setupAuthListener() {
    supabase.auth.onAuthStateChange((event, session) => {
      this.currentSession = session;
      if (session) {
        this.sessionCache = { session };
        this.sessionCacheTime = Date.now();
        // Note: Don't call setSession here as it causes infinite loops
      } else {
        this.sessionCache = null;
        this.sessionCacheTime = 0;
      }
    });

    // Also try to get initial session immediately
    this.initializeSession();
  }

  async initializeSession() {
    // Try to manually parse session from localStorage instead of using getSession()
    try {
      const storedSession = SUPABASE_SESSION_KEY
        ? localStorage.getItem(SUPABASE_SESSION_KEY)
        : null;
      if (storedSession) {
        const sessionData = JSON.parse(storedSession);
        // Check if session is not expired
        const now = Math.floor(Date.now() / 1000);
        if (sessionData.expires_at && sessionData.expires_at > now) {
          this.currentSession = sessionData;
          this.sessionCache = { session: sessionData };
          this.sessionCacheTime = Date.now();
          // Note: Don't call setSession here as it can cause auth loops
        }
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        logger.warn(
          "[AuthService] Failed to initialize session from storage:",
          err,
        );
      }
    }
  }

  /**
   * Get current authentication session
   * - Uses a timeout to avoid hanging the app when Supabase is slow to restore session
   * - Caches the last result briefly to reduce pressure on the SDK
   * @returns {Promise<{ session: any }>} data object with { session }
   */
  async getSession() {
    try {
      this._logRequest("Getting current session");

      // 1) Prefer the in-memory session from the auth listener
      if (this.currentSession) {
        return { session: this.currentSession };
      }

      // 2) Return cached session quickly if still fresh
      const startMs = Date.now();
      if (
        this.sessionCache &&
        startMs - this.sessionCacheTime < this.sessionCacheDuration
      ) {
        return this.sessionCache;
      }

      // 3) Try to read and validate the session from localStorage
      const stored = SUPABASE_SESSION_KEY
        ? localStorage.getItem(SUPABASE_SESSION_KEY)
        : null;
      let parsedSession = null;
      if (stored) {
        try {
          const obj = JSON.parse(stored);
          // Supabase v2 typically stores { currentSession, expiresAt, ... }
          // but older/newer formats may store the session directly.
          const candidate = obj?.currentSession || obj;

          const nowSec = Math.floor(Date.now() / 1000);
          const expSec =
            candidate?.expires_at || obj?.expiresAt || candidate?.exp || null;
          // If we have an expiry and it's in the future, accept the session
          if (!expSec || expSec > nowSec) {
            parsedSession = candidate;
          }
        } catch (e) {
          // ignore parse errors and fall through
        }
      }

      if (parsedSession) {
        this.currentSession = parsedSession;
        this.sessionCache = { session: parsedSession };
        this.sessionCacheTime = Date.now();
        return this.sessionCache;
      }

      // 4) Last resort: ask Supabase with a short timeout to avoid hanging the app
      const withTimeout = (p, ms) =>
        Promise.race([
          p,
          new Promise((resolve) =>
            setTimeout(
              () => resolve({ data: { session: null }, error: null }),
              ms,
            ),
          ),
        ]);

      const { data } = await withTimeout(supabase.auth.getSession(), 800);
      const sess = data?.session || null;

      if (sess) {
        this.currentSession = sess;
        this.sessionCache = { session: sess };
        this.sessionCacheTime = Date.now();
      } else {
        this.sessionCache = null;
        this.sessionCacheTime = 0;
      }

      this._logSuccess("Session retrieved", {
        hasSession: !!sess,
        userId: sess?.user?.id,
        elapsedMs: Date.now() - startMs,
      });

      return this.sessionCache || { session: null };
    } catch (error) {
      this._logError("Error getting session", error);
      // Fail safe: do not block requests; return null session
      return { session: null };
    }
  }

  /**
   * Get current user
   * @returns {Promise<Object>} User data
   */
  async getCurrentUser() {
    try {
      this._logRequest("Getting current user");

      const token = localStorage.getItem("auth_token");
      if (!token) {
        return { user: null };
      }

      // Use backend verify endpoint
      const response = await fetch("/api/auth/verify", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // Handle 401/403 by clearing invalid token
        if (response.status === 401 || response.status === 403) {
          this._logError("Session invalid, clearing token");
          localStorage.removeItem("auth_token");
          return { user: null };
        }
        throw new Error("Failed to verify token");
      }

      const result = await response.json();

      this._logSuccess("User retrieved successfully", {
        userId: result.user?.id,
        email: result.user?.email,
      });

      return { user: result.user };
    } catch (error) {
      this._logError("Error getting current user", error);
      // Clear token on any error
      localStorage.removeItem("auth_token");
      return { user: null };
    }
  }

  /**
   * Sign in with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Sign in result
   */
  async signIn(email, password) {
    try {
      this._logRequest("Signing in user", { email });

      // Use backend auth endpoint instead of direct Supabase
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Login failed");
      }

      // Store token in localStorage for subsequent requests
      if (result.token) {
        localStorage.setItem("auth_token", result.token);
      }

      // After successful login, check player status in our database.
      // (Bug fix: the previous version referenced `data.user` but the
      // response variable here is `result`. `data` was a leftover from
      // the old Supabase-direct pattern.)
      if (result.user) {
        try {
          const { data: playerData, error: playerError } = await supabase
            .from("players")
            .select("status, suspension_reason")
            .eq("id", result.user.id)
            .single();

          if (playerError) {
            this._logError(
              "Failed to check player status after login",
              playerError,
            );
            // Don't fail login for database errors, let middleware handle it
          } else if (playerData && playerData.status !== "active") {
            // Player is suspended/banned/inactive - sign them out immediately and clear all sessions
            try {
              await supabase.auth.signOut();

              // Also clear any local storage/session storage
              if (typeof window !== "undefined") {
                localStorage.clear();
                sessionStorage.clear();
              }
            } catch (signOutError) {
              this._logError("Failed to sign out suspended user", signOutError);
            }

            const message =
              playerData.status === "suspended"
                ? `Account is suspended${playerData.suspension_reason ? ": " + playerData.suspension_reason : ""}`
                : playerData.status === "banned"
                  ? "Account is banned"
                  : "Account is inactive";

            this._logError("Login blocked - account not active", {
              status: playerData.status,
              reason: playerData.suspension_reason,
            });

            throw new Error(message);
          }
        } catch (statusError) {
          // If it's our custom error message, re-throw it
          if (
            statusError.message.includes("suspended") ||
            statusError.message.includes("banned") ||
            statusError.message.includes("inactive")
          ) {
            throw statusError;
          }
          // For other errors, log but don't fail login
          this._logError(
            "Error checking player status after login",
            statusError,
          );
        }
      }

      this._logSuccess("User signed in successfully", {
        userId: result.player?.id,
        email: result.player?.email,
      });

      return {
        user: result.player,
        session: { access_token: result.token },
      };
    } catch (error) {
      this._logError("Error signing in user", error);
      throw error;
    }
  }

  /**
   * Sign up new user
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {Object} userData - Additional user data
   * @returns {Promise<Object>} Sign up result
   */
  async signUp(email, password, userData = {}) {
    try {
      this._logRequest("Signing up new user", {
        email,
        userData: Object.keys(userData),
      });

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
        },
      });

      if (error) {
        throw error;
      }

      this._logSuccess("User signed up successfully", {
        userId: data.user?.id,
        email: data.user?.email,
        needsConfirmation: !data.session,
      });

      return data;
    } catch (error) {
      this._logError("Error signing up user", error);
      throw error;
    }
  }

  /**
   * Sign out current user
   * @returns {Promise<Object>} Sign out result
   */
  async signOut() {
    try {
      this._logRequest("Signing out user");

      // Attempt Supabase sign-out, but never let it block UI: enforce a short timeout
      const timeoutMs = 1200;
      try {
        await Promise.race([
          supabase.auth.signOut(),
          new Promise((resolve) => setTimeout(resolve, timeoutMs)),
        ]);
      } catch (e) {
        this._logError(
          "Supabase signOut failed (continuing with local cleanup)",
          e,
        );
      }

      // Clear local token and any cached session artifacts
      try {
        if (typeof window !== "undefined") {
          localStorage.removeItem("auth_token");
          if (SUPABASE_SESSION_KEY)
            localStorage.removeItem(SUPABASE_SESSION_KEY);
          sessionStorage.clear();
        }
      } catch (storageErr) {
        this._logError(
          "Error clearing local/session storage during signOut",
          storageErr,
        );
      }

      // Reset in-memory caches
      this.currentSession = null;
      this.sessionCache = null;
      this.sessionCacheTime = 0;

      this._logSuccess("User signed out successfully");

      return { error: null };
    } catch (error) {
      this._logError("Error signing out user", error);
      throw error;
    }
  }

  /**
   * Listen to authentication state changes
   * @param {Function} callback - Callback function for auth changes
   * @returns {Function} Unsubscribe function
   */
  onAuthStateChange(callback) {
    this._logRequest("Setting up auth state listener");

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      this._logRequest("Auth state changed", {
        event,
        userId: session?.user?.id,
      });
      callback(event, session);
    });

    // Return unsubscribe function
    return () => {
      this._logRequest("Removing auth state listener");
      subscription.unsubscribe();
    };
  }

  /**
   * Reset password for user
   * @param {string} email - User email
   * @returns {Promise<Object>} Reset result
   */
  async resetPassword(email) {
    try {
      this._logRequest("Resetting password", { email });

      const redirectTo =
        typeof window !== "undefined"
          ? `${window.location.origin}/auth/callback`
          : undefined;
      const { data, error } = await supabase.auth.resetPasswordForEmail(
        email,
        redirectTo ? { redirectTo } : undefined,
      );

      if (error) {
        throw error;
      }

      this._logSuccess("Password reset email sent successfully", { email });

      return data;
    } catch (error) {
      this._logError("Error resetting password", error);
      throw error;
    }
  }

  /**
   * Update user password
   * @param {string} password - New password
   * @returns {Promise<Object>} Update result
   */
  async updatePassword(password) {
    try {
      this._logRequest("Updating user password");

      const { data, error } = await supabase.auth.updateUser({
        password,
      });

      if (error) {
        throw error;
      }

      this._logSuccess("Password updated successfully");

      return data;
    } catch (error) {
      this._logError("Error updating password", error);
      throw error;
    }
  }

  /**
   * Structured logging for auth operations. Routes through the shared logger
   * (environment-aware: info/success/warn are dev-only; errors always emit,
   * even in production). Request/success traces are gated behind enableLogging
   * to avoid noise; errors are ALWAYS logged so suspended-login attempts,
   * token invalidation, and post-login status-check failures are never silent.
   * @private
   */
  _logRequest(message, data) {
    if (!this.enableLogging) return;
    logger.debug(`[${this.serviceName}] ${message}`, data || null);
  }

  _logSuccess(message, data) {
    if (!this.enableLogging) return;
    logger.success(`[${this.serviceName}] ${message}`, data || null);
  }

  _logError(message, error) {
    // Always emit errors (logger.error ignores enableLogging by design).
    logger.error(`[${this.serviceName}] ${message}`, error || null);
  }
}

// Export singleton instance
export default new AuthService();
